/**
 * SEND review dates — the client's half.
 *
 * A deliberate mirror of the same names in `functions/src/send/sendLogic.ts`.
 * The two workspaces build separately and neither can import the other, so the
 * register would otherwise colour a row green that the server considers
 * overdue, or offer a cadence the sweep silently rewrites.
 * `scripts/send-test.mjs` imports both copies and fails if they disagree, so
 * drift is caught by `npm run verify` rather than by a SENCo who trusted a
 * green row.
 *
 * Keep this file free of anything the server half does not also have. In
 * particular the client NEVER computes `overdue` for storage — it reads the
 * field the sweep wrote and uses these helpers only to decide what colour to
 * paint it.
 */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function sendToday(nowMs: number = Date.now()): string {
  return new Date(nowMs).toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

export function isValidDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

function toParts(date: string): { y: number; m: number; d: number } {
  const m = DATE_RE.exec(date);
  if (!m) throw new Error(`not a date: ${date}`);
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/** Mirrors the server helper of the same name. */
export function addDays(date: string, days: number): string {
  const { y, m, d } = toParts(date);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  const pad = (n: number, w: number) => String(n).padStart(w, '0');
  return `${pad(dt.getUTCFullYear(), 4)}-${pad(dt.getUTCMonth() + 1, 2)}-${pad(dt.getUTCDate(), 2)}`;
}

export function daysBetween(from: string, to: string): number {
  const a = toParts(from);
  const b = toParts(to);
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000);
}

export function isOverdue(due: string | null | undefined, today: string): boolean {
  if (!due || !isValidDate(due)) return false;
  return due < today;
}

export function isDueSoon(due: string | null | undefined, today: string, leadDays: number): boolean {
  if (!due || !isValidDate(due)) return false;
  if (due < today) return false;
  return daysBetween(today, due) <= leadDays;
}

export type ReviewUrgency = 'overdue' | 'dueSoon' | 'scheduled' | 'none';

export function reviewUrgency(
  due: string | null | undefined, today: string, leadDays: number,
): ReviewUrgency {
  if (!due || !isValidDate(due)) return 'none';
  if (isOverdue(due, today)) return 'overdue';
  if (isDueSoon(due, today, leadDays)) return 'dueSoon';
  return 'scheduled';
}

/** How a due date reads to a human. "Overdue by 3 days" is the whole product in
 *  four words, so it must never say that about a date that is not past. */
export function dueLabel(due: string | null | undefined, today: string): string {
  if (!due || !isValidDate(due)) return 'No date set';
  const days = daysBetween(today, due);
  if (days < 0) return `Overdue by ${-days} ${-days === 1 ? 'day' : 'days'}`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days <= 31) return `Due in ${days} days`;
  return `Due ${formatDate(due)}`;
}

/** yyyy-mm-dd → "3 September 2026". Built from the parts rather than
 *  `new Date(str)` so it cannot shift a day either side of midnight. */
export function formatDate(date: string | null | undefined): string {
  if (!date || !isValidDate(date)) return '—';
  const { y, m, d } = toParts(date);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}
